import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHead from '../components/common/SEOHead';
import { blogPosts, blogCategories, popularTags } from '../data/blogData';

const Blog = () => {
  const { category } = useParams();
  const [filteredPosts, setFilteredPosts] = useState(blogPosts);
  const [selectedCategory, setSelectedCategory] = useState(category || 'all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let filtered = blogPosts;
    
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(post => 
        post.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }
    
    if (searchTerm) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    setFilteredPosts(filtered);
  }, [selectedCategory, searchTerm]);

  const handleCategoryClick = (categorySlug) => {
    setSelectedCategory(categorySlug);
  };

  const handlePostClick = (postTitle) => {
  };

  const seoData = {
    title: "Marble Blog - Expert Tips, Guides & Design Inspiration | Makrana Marble",
    description: "Discover expert marble guides, stunning design inspiration, maintenance tips, and latest trends. Learn from India's leading marble artisans and designers.",
    keywords: "marble blog, marble guides, design inspiration, marble tips, marble maintenance, architecture blog, interior design, marble trends",
    url: "https://makrana-marble.com/blog",
    type: "website"
  };

  return (
    <>
      <SEOHead {...seoData} />
      <div className="min-h-screen bg-gray-50 pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-gray-900 to-gray-700 text-white py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Marble Insights & Design Inspiration
              </h1>
              <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-200">
                Expert guides, stunning designs, and insider tips from India's finest marble artisans
              </p>
              
              {/* Search Bar */}
              <div className="max-w-md mx-auto">
                <input
                  type="text"
                  placeholder="Search articles, guides, tips..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </motion.div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <main className="lg:w-2/3">
              {/* Category Filter */}
              <div className="mb-8">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleCategoryClick('all')}
                    className={`px-4 py-2 rounded-full font-medium transition-colors ${
                      selectedCategory === 'all'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    All Articles
                  </button>
                  {blogCategories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategoryClick(cat.slug)}
                      className={`px-4 py-2 rounded-full font-medium transition-colors ${
                        selectedCategory === cat.slug
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {cat.name} ({cat.count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Articles Grid */}
              <div className="grid gap-8">
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                  >
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                          {post.category}
                        </span>
                        <span className="mx-2">•</span>
                        <span>{post.publishDate}</span>
                        <span className="mx-2">•</span>
                        <span>{post.readTime}</span>
                      </div>
                      
                      <h2 className="text-2xl font-bold text-gray-900 mb-3 hover:text-blue-600 transition-colors">
                        <Link 
                          to={`/blog/${post.slug}`}
                          onClick={() => handlePostClick(post.title)}
                        >
                          {post.title}
                        </Link>
                      </h2>
                      
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 4).map((tag) => (
                          <span
                            key={tag}
                            className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          By {post.author}
                        </span>
                        <Link
                          to={`/blog/${post.slug}`}
                          onClick={() => handlePostClick(post.title)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                          Read More
                        </Link>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>

              {filteredPosts.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">
                    No articles found matching your search criteria.
                  </p>
                </div>
              )}
            </main>

            {/* Sidebar */}
            <aside className="lg:w-1/3">
              {/* Popular Tags */}
              <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Popular Topics
                </h3>
                <div className="flex flex-wrap gap-2">
                  {popularTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setSearchTerm(tag)}
                      className="bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-800 px-3 py-1 rounded-full text-sm transition-colors"
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Featured Articles */}
              <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Featured Articles
                </h3>
                {blogPosts.filter(post => post.featured).slice(0, 3).map((post) => (
                  <div key={post.id} className="mb-4 pb-4 border-b border-gray-200 last:border-b-0">
                    <h4 className="font-medium text-gray-900 mb-2 hover:text-blue-600 transition-colors">
                      <Link 
                        to={`/blog/${post.slug}`}
                        onClick={() => handlePostClick(post.title)}
                      >
                        {post.title}
                      </Link>
                    </h4>
                    <p className="text-sm text-gray-600">
                      {post.readTime} • {post.category}
                    </p>
                  </div>
                ))}
              </div>

              {/* Newsletter Signup */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                <h3 className="text-xl font-bold mb-4">
                  Get Design Inspiration
                </h3>
                <p className="mb-4 text-blue-100">
                  Subscribe to receive latest marble trends, design tips, and exclusive offers.
                </p>
                <form className="space-y-3">
                  <input
                    type="email"
                    placeholder="Your email address"
                    className="w-full px-4 py-2 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                  />
                  <button
                    type="submit"
                    className="w-full bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                  >
                    Subscribe Now
                  </button>
                </form>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default Blog;
